import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import React, { useState } from "react";
import { Label } from "../templates";
import { AuditLogEntryField as AuditLogEntryFieldHelper } from "./helpers";
import { ArrayFieldProps, AuditLogEntryArrayFieldOptions, AuditLogEntryFormData } from "./models";

const styles = (theme: Theme) =>
  createStyles({
    display: {
      color: theme.palette.text.primary,
      marginTop: theme.spacing(2),
      padding: "7px 0 9px",
    },
    logEntry: {
      "&:last-child": {
        marginBottom: "-9px",
      },
    },
    expandButton: {
      margin: "-9px 0",
    },
  });

interface AuditLogEntryArrayFieldProps extends ArrayFieldProps<AuditLogEntryFormData>, WithStyles<typeof styles> {}

export const AuditLogEntryArrayFieldComponent = (props: AuditLogEntryArrayFieldProps) => {
  const { id, schema, uiSchema, formData = [], classes, formContext } = props;
  const { getAuditLogEntryLabelAndClassName } = formContext;

  const label = schema.title;
  const options = uiSchema["ui:options"] as AuditLogEntryArrayFieldOptions | undefined;

  if (!getAuditLogEntryLabelAndClassName) {
    throw Error("getAuditLogEntryLabelAndClassName not found on formContext.");
  }

  const displayLimit = options?.displayLimit;
  const emptyValue = options?.emptyValue ?? "(None)";

  // By default, set expanded true if no Display Limit is in-effect, or a Display Limit is in-effect, but the number
  // of Log Entries does not exceed the limit
  const [expanded, setExpanded] = useState<boolean>(displayLimit ? formData.length <= displayLimit : true);

  const logEntries = !expanded && displayLimit ? formData.filter((logEntry, index) => index < displayLimit) : formData;
  const showExpandButton = !expanded && displayLimit && formData.length > displayLimit;

  const handleExpandButtonClick = () => {
    setExpanded(true);
  };

  return (
    <FormControl fullWidth={true} margin="none">
      {label && <Label fieldId={id} label={label} labelOnly={true} />}
      <div className={classes.display}>
        {logEntries.length ? (
          <>
            {logEntries.map((auditLogEntry: AuditLogEntryFormData, index: number) => {
              const { entryType, at, userName, personName, comment } = auditLogEntry;
              const entryTypeLabelAndClassName = getAuditLogEntryLabelAndClassName(entryType);
              return (
                <AuditLogEntryFieldHelper
                  key={`alek_${index + 1}`}
                  entryTypeLabelAndClassName={entryTypeLabelAndClassName}
                  at={at}
                  userName={userName}
                  personName={personName}
                  comment={comment}
                  className={classes.logEntry}
                />
              );
            })}
            {showExpandButton ? (
              <Button fullWidth={true} className={classes.expandButton} onClick={handleExpandButtonClick}>
                <ExpandMoreIcon />
                &nbsp;&nbsp;Show All (+{formData.length - displayLimit!})
              </Button>
            ) : null}
          </>
        ) : (
          <Typography color="textPrimary">{emptyValue}</Typography>
        )}
      </div>
    </FormControl>
  );
};

export const AuditLogEntryArrayField = withStyles(styles)(AuditLogEntryArrayFieldComponent);
